import { useState, useRef, useCallback } from "react";
import { clamp } from "utils/utils";

interface VerticalSliderProps {
  value: number;
  setValue:
    | React.Dispatch<React.SetStateAction<number>>
    | ((value: number) => void);
  min?: number;
  max?: number;
  step?: number;
  label?: string;
  accentColor?: string;
}

const VerticalSlider: React.FC<VerticalSliderProps> = ({
  value,
  setValue,
  min = 0,
  max = 100,
  step = 1,
  label = "Value",
  accentColor = "#B54810",
}) => {
  // States definition
  const [dragging, setDragging] = useState(false);

  // Reference definition
  const trackRef = useRef<HTMLDivElement>(null);

  // Parameters
  const relativeValue = ((value - min) / (max - min)) * 100;

  const getValueFromEvent = useCallback(
    (clientY: number) => {
      const track = trackRef.current;
      if (!track) return value;
      const rect = track.getBoundingClientRect();
      // Inverted: top = max, bottom = min
      const ratio = clamp(1 - (clientY - rect.top) / rect.height, 0, 1);
      const raw = min + ratio * (max - min);
      return Math.round(raw / step) * step;
    },
    [min, max, step, value],
  );

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      setDragging(true);
      setValue(getValueFromEvent(event.clientY));

      // Function that sets the value
      const handleMove = (e: PointerEvent) => {
        setValue(getValueFromEvent(e.clientY));
      };

      const handleUp = () => {
        setDragging(false);
        window.removeEventListener("pointermove", handleMove);
        window.removeEventListener("pointerup", handleUp);
      };

      window.addEventListener("pointermove", handleMove);
      window.addEventListener("pointerup", handleUp);
    },
    [getValueFromEvent],
  );

  /**
   * Executes a function for when the pointer is moving
   */
  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!dragging) return;
      setValue(getValueFromEvent(event.clientY));
    },
    [dragging, getValueFromEvent],
  );

  /**
   * Executes the function when the pointer is up
   */
  const handlePointerUp = useCallback(() => setDragging(false), []);

  /**
   * Executes the function when a arrow key is clicked
   */
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const actions: { [key: string]: () => number } = {
        ArrowUp: () => clamp(value + step, min, max),
        ArrowDown: () => clamp(value - step, min, max),
        ArrowRight: () => clamp(value + step, min, max),
        ArrowLeft: () => clamp(value - step, min, max),
        Home: () => min,
        End: () => max,
      };
      if (actions[event.key]) {
        event.preventDefault();
        setValue(actions[event.key]());
      }
    },
    [value, step, min, max],
  );

  return (
    <div
      ref={trackRef}
      role="slider"
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={value}
      aria-label={label}
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onKeyDown={handleKeyDown}
      className="relative flex justify-center w-5 flex-1 rounded-sm outline-none select-none touch-none"
      style={{ cursor: dragging ? "grabbing" : "grab" }}
    >
      {/* Track background */}
      <div className="absolute inset-x-0 mx-auto w-2 h-full bg-neutral-100 rounded-full" />

      {/* Filled portion (bottom to relativeValue) */}
      <div
        className="absolute bottom-0 mx-auto w-2 rounded-full"
        style={{
          height: `${relativeValue}%`,
          // background: `linear-gradient(to top, color-mix(in srgb, ${accentColor} 60%, transparent), ${accentColor})`,
          background: accentColor,
        }}
      />

      {/* Thumb */}
      <div
        className="absolute left-1/2 -translate-x-1/2 rounded-full border-2 bg-white flex items-center justify-center"
        style={{
          bottom: `${relativeValue}%`,
          transform: `translateX(-50%) translateY(50%)`,
          width: dragging ? 22 : 18,
          height: dragging ? 22 : 18,
          borderColor: accentColor,
          boxShadow: dragging
            ? `0 0 0 4px ${accentColor}22, 0 0 12px ${accentColor}44`
            : `0 0 0 0px transparent`,
        }}
      />
    </div>
  );
};

export default VerticalSlider;
